import axios from 'axios';
import { API_BASE_URL } from '../config';

const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  const apiKey = (() => {
    try {
      return localStorage.getItem('ai_tech_api_key');
    } catch (err) {
      console.warn('Unable to read api key from storage', err);
      return null;
    }
  })();

  if (apiKey) {
    config.headers = config.headers || {};
    config.headers['X-API-Key'] = apiKey;
  }

  return config;
});

const extractError = (error) => {
  if (error?.response?.data?.detail) {
    return Array.isArray(error.response.data.detail)
      ? error.response.data.detail.join(', ')
      : error.response.data.detail;
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  return error.message || 'Request failed';
};

export const fetchMaskingSettings = async () => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/settings');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateMaskingSettings = async (payload) => {
  try {
    const { data } = await client.put('/api/v1/ai-image-masking/settings', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listSwatches = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/swatches', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const createSwatch = async ({ file, name, description }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);

    const { data } = await client.post('/api/v1/ai-image-masking/swatches', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listSwatchesV2 = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/swatches/v2', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const createSwatchV2 = async ({ file, name, description }) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);

    const { data } = await client.post('/api/v1/ai-image-masking/swatches/v2', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteSwatchV2 = async (swatchId) => {
  try {
    await client.delete(`/api/v1/ai-image-masking/swatches/${swatchId}`);
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteSwatchesV2 = async (ids = []) => {
  try {
    await client.post('/api/v1/ai-image-masking/swatches/bulk-delete', { ids });
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteSwatch = async (swatchId) => {
  try {
    const { data } = await client.delete(`/api/v1/ai-image-masking/swatches/${swatchId}`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const processTryOn = async ({ environmentFile, swatchId, prompt, userId, agentTag, forceOverwrite = false }) => {
  try {
    const formData = new FormData();
    formData.append('environment_image', environmentFile);
    formData.append('swatch_id', swatchId);
    if (userId) {
      formData.append('user_id', userId);
    }
    if (prompt) {
      formData.append('prompt', prompt);
    }
    if (agentTag) {
      formData.append('agent_tag', agentTag);
    }
    formData.append('force_overwrite', forceOverwrite ? 'true' : 'false');

    const { data } = await client.post('/api/v1/ai-image-masking/try-on', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (error) {
    if (error?.response?.status === 409 && error?.response?.data?.detail?.code === 'tryon_exists') {
      const err = new Error(error.response.data.detail.message || 'Existing try-on found');
      err.code = error.response.data.detail.code;
      err.payload = error.response.data.detail;
      throw err;
    }
    throw new Error(extractError(error));
  }
};

export const listGallery = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/gallery', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listGalleryUsers = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/gallery/users', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listAgents = async () => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/agents');
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const createAgent = async (payload) => {
  try {
    const { data } = await client.post('/api/v1/ai-image-masking/agents', payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const updateAgent = async (agentId, payload) => {
  try {
    const { data } = await client.put(`/api/v1/ai-image-masking/agents/${agentId}`, payload);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const deleteAgent = async (agentId) => {
  try {
    await client.delete(`/api/v1/ai-image-masking/agents/${agentId}`);
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listAgentHistory = async (agentId) => {
  try {
    const { data } = await client.get(`/api/v1/ai-image-masking/agents/${agentId}/history`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const fetchSettingsHistory = async (params = {}) => {
  try {
    const { data } = await client.get('/api/v1/ai-image-masking/settings/history', { params });
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const restoreSettingsVersion = async (historyId) => {
  try {
    const { data } = await client.post(`/api/v1/ai-image-masking/settings/history/${historyId}/restore`);
    return data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

